import React, { useContext, useState } from 'react'

import { BuildingOfficeLineIcon, IconSizes } from '@inno-ui/icons'
import { toast } from '@inno-ui/toast'
import { useTranslation } from 'react-i18next'
import { checkToken } from 'shared/api/auth/requests'
import { changeUserPartner } from 'shared/api/user/requests/changePartner'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { UserContext } from 'shared/contexts/User'
import { UserPartner } from 'shared/types/user'
import { lsSetItem } from 'shared/utils/local-storage'

import { ProfileMenuRow } from '../ProfileMenuRow/ProfileMenuRow'
import styles from './OrganizationSection.module.scss'

interface Props {
  companies: UserPartner[]
  selectedCompany?: UserPartner
}

const OrganizationSection: React.FC<Props> = ({
  companies,
  selectedCompany
}) => {
  const { t } = useTranslation()
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  const { user } = useContext(UserContext)

  const onCompanyChange = async (company: UserPartner) => {
    if (!user || isLoggingIn) return

    setIsLoggingIn(true)

    try {
      const data = await changeUserPartner({
        userId: user.id,
        partnerId: company.id
      })

      const { data: newUser } = await checkToken({
        token: data.access_token
      })

      lsSetItem(LocalStorageKey.USER, {
        ...newUser.user,
        original_organization_id: newUser.original_organization_id,
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        isLoggedIn: true,
        tokenReceiveTime: new Date().getTime()
      })

      window.location.reload()
    } catch {
      toast.error({ title: t('navbar.userMenu.organizationSwitchFailed') })
    } finally {
      setIsLoggingIn(false)
    }
  }

  return (
    <ProfileMenuRow
      label={selectedCompany?.name || t('navbar.userMenu.myOrganization')}
      actionLabel={t('navbar.userMenu.changeOrganization')}
      icon={
        selectedCompany?.logo ? (
          <img
            className={styles.companyLogo}
            src={selectedCompany.logo}
            alt=''
            width={16}
            height={16}
          />
        ) : (
          <BuildingOfficeLineIcon size={IconSizes.Size16} />
        )
      }
      contextMenuProps={{
        disabled: isLoggingIn,
        options: companies.map(company => ({
          label: company.name,
          value: company.id,
          trailingText: user?.organization_id === company.id ? '✔️' : '',
          onClick: () => onCompanyChange(company)
        }))
      }}
    />
  )
}

export { OrganizationSection }
